// src/pages/DocumentManagementPage.jsx
// ============================================================
// DOCUMENT MANAGEMENT PAGE - EA CONSISTENCY + VALUE SIGNALING
// 
// Refactored per ChatGPT spec:
// - "Evidence Injection" UX framing
// - Value-oriented document cards (not file table)
// - DVA design system (Navy + Orange)
// - Strategic copy throughout
// - Collapsible "How it works"
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { EA_CLIENT_ID } from '@/prototype/constants/eaClient';
import { io } from 'socket.io-client';
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Info,
  Sparkles,
  Eye,
  Download,
  Trash2,
  Loader,
  X,
  File
} from 'lucide-react';

// ============================================================
// DVA DESIGN SYSTEM COLORS
// ============================================================
const DVA_COLORS = {
  navy: '#18365E',
  orange: '#FF6E4C',
  orangeLight: '#FFF4F1',
  slate: '#717182',
  slateLight: '#F5F7FA',
  border: '#E5E7EB',
  success: '#10B981',
  successLight: '#ECFDF5',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  error: '#EF4444',
  errorLight: '#FEF2F2',
};

const SOCKET_BASE_URL = import.meta.env.VITE_SOCKET_URL;
// Socket.IO path; differs from the default when the worker is reached through the load balancer
const SOCKET_PATH = import.meta.env.VITE_SOCKET_PATH || '/socket.io';
const IS_DEV_MODE = Boolean(import.meta.env.DEV);

const ERROR_STATUS_LABELS = {
  failed: 'Processing failed',
  scan_failed: 'Scan failed',
  quarantined: 'Quarantined',
};

const normalizeIncomingStatus = (rawStatus) => {
  const status = String(rawStatus || '').trim().toLowerCase();
  if (status === 'generating...') return 'generating';
  return status;
};

const getQueueStatusMeta = (rawStatus) => {
  const status = normalizeIncomingStatus(rawStatus);

  if (status === 'processed') {
    return {
      status: 'complete',
      progress: 100,
      statusLabel: 'Ready',
      error: null,
      backendStatus: 'processed',
    };
  }

  if (status === 'failed' || status === 'scan_failed' || status === 'quarantined') {
    return {
      status: 'error',
      progress: 100,
      statusLabel: ERROR_STATUS_LABELS[status] || 'Processing failed',
      error: ERROR_STATUS_LABELS[status] || 'Processing failed',
      backendStatus: status,
    };
  }

  const labelMap = {
    uploaded: 'Uploaded',
    scanning: 'Scanning',
    scan_unknown: 'Scan status unknown',
    scanned: 'Scanned',
    generating: 'Generating...',
    processing: 'Processing...',
  };

  const progressMap = {
    uploaded: 35,
    scanning: 45,
    scan_unknown: 50,
    scanned: 60,
    generating: 75,
    processing: 85,
  };

  return {
    status: 'processing',
    progress: progressMap[status] ?? 55,
    statusLabel: labelMap[status] || 'Processing...',
    error: null,
    backendStatus: status || 'processing',
  };
};

const mergeRealtimeStatusIntoDocument = (doc, rawStatus, updatedAt = null) => {
  if (!doc) return doc;
  const normalizedStatus = normalizeIncomingStatus(rawStatus);
  const safeStatus = normalizedStatus === 'generating' ? 'Generating...' : normalizedStatus;

  return {
    ...doc,
    status: safeStatus || doc.status,
    updated_at: updatedAt || doc.updated_at,
  };
};

// ============================================================
// EA FOCUS MODE DETECTION HELPER
// ============================================================
function useEAFocusMode() {
  const location = useLocation();
  const locationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);

  const isEA = locationState.fromEA === true || searchParams.get('fromEA') === 'true';
  const returnTo = locationState.returnTo || '/ea/personalization';
  const restoreScrollY = locationState.restoreScrollY || 0;

  return { isEA, returnTo, restoreScrollY };
}

// ============================================================
// EA FOCUS BANNER COMPONENT (Minimized breadcrumb per spec)
// ============================================================
function EAFocusBanner({ returnTo, restoreScrollY }) {
  const navigate = useNavigate();

  const handleBackClick = () => {
    // Always pass refresh so Personalize reloads doc % even when scrollY is 0
    navigate(returnTo, {
      state: { restoreScrollY: restoreScrollY || 0, refresh: true },
    });
  };

  return (
    <div style={{ backgroundColor: DVA_COLORS.navy }} className="text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Personalize
          </button>

          {/* Minimal breadcrumb - just context hint */}
          <span className="text-xs text-white/50">
            EA Personalization
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DOCUMENT CARD COMPONENT (Value-Oriented with Signals Badge)
// ============================================================

/** Prefer API `signal_count` (matches extracted signals). `stats.fact_count` can differ. */
function resolveDocumentSignalCount(doc) {
  const signalsLen = Array.isArray(doc?.signals) ? doc.signals.length : null;
  const raw =
    doc?.signal_count ??
    doc?.stats?.signal_count ??
    signalsLen ??
    doc?.fact_count ??
    doc?.stats?.fact_count;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n);
}

function DocumentCard({ doc, onViewDetails, onRemove, onDownload }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadUrl =
    doc?.download_url ||
    doc?.downloadUrl ||
    doc?.file_url ||
    doc?.fileUrl ||
    doc?.signed_url ||
    doc?.signedUrl ||
    doc?.public_url ||
    doc?.publicUrl;

  const downloadExpiry = doc?.download_url_expires_at
    ? new Date(doc.download_url_expires_at)
    : null;
  const isDownloadExpired =
    downloadExpiry &&
    !Number.isNaN(downloadExpiry.getTime()) &&
    downloadExpiry.getTime() <= Date.now();
  const canDownload = Boolean(onDownload && downloadUrl && !isDownloadExpired);

  const statusConfig = {
    'uploaded': {
      icon: Clock,
      label: 'Uploaded',
      bgColor: DVA_COLORS.warningLight,
      textColor: DVA_COLORS.warning
    },
    'scanning': {
      icon: Loader,
      label: 'Scanning...',
      bgColor: DVA_COLORS.warningLight,
      textColor: DVA_COLORS.warning
    },
    'scan_unknown': {
      icon: AlertCircle,
      label: 'Scan unknown',
      bgColor: DVA_COLORS.warningLight,
      textColor: DVA_COLORS.warning
    },
    'scanned': {
      icon: CheckCircle,
      label: 'Scanned',
      bgColor: DVA_COLORS.slateLight,
      textColor: DVA_COLORS.slate
    },
    'generating': {
      icon: Loader,
      label: 'Generating...',
      bgColor: DVA_COLORS.orangeLight,
      textColor: DVA_COLORS.orange
    },
    'processing': {
      icon: Loader,
      label: 'Processing...',
      bgColor: DVA_COLORS.orangeLight,
      textColor: DVA_COLORS.orange
    },
    'processed': {
      icon: CheckCircle,
      label: 'Ready',
      bgColor: DVA_COLORS.successLight,
      textColor: DVA_COLORS.success
    },
    'failed': {
      icon: XCircle,
      label: 'Processing failed',
      bgColor: DVA_COLORS.errorLight,
      textColor: DVA_COLORS.error
    },
    'scan_failed': {
      icon: XCircle,
      label: 'Scan failed',
      bgColor: DVA_COLORS.errorLight,
      textColor: DVA_COLORS.error
    },
    'quarantined': {
      icon: AlertCircle,
      label: 'Quarantined',
      bgColor: DVA_COLORS.errorLight,
      textColor: DVA_COLORS.error
    }
  };

  const normalizedDocStatus = normalizeIncomingStatus(doc.status);
  const config = statusConfig[normalizedDocStatus] || statusConfig['uploaded'];
  const StatusIcon = config.icon;
  const isProcessing = ['uploaded', 'scanning', 'scan_unknown', 'scanned', 'generating', 'processing'].includes(normalizedDocStatus);
  const isComplete = normalizedDocStatus === 'processed';

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp.value || timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileTypeLabel = (fileType) => {
    const types = { 'txt': 'TXT', 'csv': 'CSV', 'pdf': 'PDF', 'md': 'MD' };
    return types[fileType] || fileType?.toUpperCase() || 'DOC';
  };

  const handleDownloadClick = async () => {
    if (!canDownload || isDownloading) return;
    try {
      setIsDownloading(true);
      await onDownload(doc);
    } finally {
      setIsDownloading(false);
    }
  };

  // Use API signal_count (aligned with extracted signals). Never invent random counts.
  const signalsFound = resolveDocumentSignalCount(doc);

  return (
    <div
      className="bg-white rounded-2xl border-2 transition-all duration-200"
      style={{
        borderColor: isHovered ? DVA_COLORS.orange : DVA_COLORS.border,
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* File Type Badge */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: DVA_COLORS.slateLight }}
          >
            <span className="text-xs font-bold" style={{ color: DVA_COLORS.navy }}>
              {getFileTypeLabel(doc.file_type)}
            </span>
          </div>

          {/* Document Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4
                  className="text-sm font-semibold truncate"
                  style={{ color: DVA_COLORS.navy }}
                >
                  {doc.title || doc.filename}
                </h4>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                    {formatDate(doc.created_at)}
                  </span>
                  {doc.bytes_total && (
                    <>
                      <span style={{ color: DVA_COLORS.border }}>•</span>
                      <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                        {formatFileSize(doc.bytes_total)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Right side: Status + Signals */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Signals Found Badge */}
                {isComplete && signalsFound > 0 && (
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded-lg"
                    style={{ backgroundColor: DVA_COLORS.orangeLight }}
                  >
                    <Sparkles className="w-3 h-3" style={{ color: DVA_COLORS.orange }} />
                    <span className="text-xs font-medium" style={{ color: DVA_COLORS.orange }}>
                      {signalsFound} signals
                    </span>
                  </div>
                )}

                {/* Status Badge */}
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                  style={{
                    backgroundColor: config.bgColor,
                    color: config.textColor,
                  }}
                >
                  <StatusIcon
                    className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`}
                  />
                  <span className="text-xs font-medium">{config.label}</span>
                </div>
              </div>
            </div>

            {/* Contribution tags for completed docs */}
            {isComplete && (
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                  Enhances:
                </span>
                {['Strategic Alignment', 'ROI Assumptions'].map((tag, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-medium px-2 py-0.5 rounded-md"
                    style={{
                      backgroundColor: DVA_COLORS.slateLight,
                      color: DVA_COLORS.navy,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions Row */}
      <div
        className="flex items-center justify-end gap-2 px-4 py-3 border-t"
        style={{ borderColor: DVA_COLORS.border }}
      >
        <button
          onClick={handleDownloadClick}
          disabled={isDownloading || !canDownload}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            color: canDownload ? DVA_COLORS.orange : DVA_COLORS.slate,
            backgroundColor: canDownload ? DVA_COLORS.orangeLight : DVA_COLORS.slateLight,
          }}
          title={isDownloadExpired ? 'Download link expired. Refresh the document list.' : undefined}
        >
          {isDownloading ? (
            <Loader className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          {isDownloadExpired ? 'Expired' : 'Download'}
        </button>
        <button
          onClick={() => onViewDetails?.(doc)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            color: DVA_COLORS.navy,
            backgroundColor: DVA_COLORS.slateLight,
          }}
        >
          <Eye className="w-3.5 h-3.5" />
          View details
        </button>
        <button
          onClick={() => onRemove?.(doc)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-red-50"
          style={{ color: DVA_COLORS.slate }}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Remove
        </button>
      </div>
    </div>
  );
}

// ============================================================
// DOCUMENT DETAILS MODAL (Right-side drawer style)
// ============================================================

const SIGNAL_TYPE_LABELS = {
  kpi: 'KPI',
  goal: 'Goal',
  risk: 'Risk',
  capability: 'Capability',
  fact: 'Fact',
  metric: 'Metric',
};

function formatSignalType(type) {
  if (!type) return 'Signal';
  const key = String(type).toLowerCase();
  return SIGNAL_TYPE_LABELS[key] ?? String(type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function resolveDocumentSignals(doc) {
  if (Array.isArray(doc?.signals)) return doc.signals.filter(Boolean);
  if (Array.isArray(doc?.stats?.signals)) return doc.stats.signals.filter(Boolean);
  return [];
}

function DocumentDetailsModal({ doc, isOpen, onClose }) {
  if (!isOpen || !doc) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.value || timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const signals = resolveDocumentSignals(doc);
  const signalsFound = resolveDocumentSignalCount(doc) || signals.length;
  const factTypes = Number(doc?.stats?.fact_types);
  const sectionCount = Number(doc?.stats?.section_count);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto"
        style={{ animation: 'slideIn 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10" style={{ borderColor: DVA_COLORS.border }}>
          <h2 className="text-lg font-semibold" style={{ color: DVA_COLORS.navy }}>
            Document Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: DVA_COLORS.slate }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: DVA_COLORS.navy }}>
              {doc.title || doc.filename}
            </h3>
            <div className="space-y-2 text-sm" style={{ color: DVA_COLORS.slate }}>
              <div className="flex justify-between">
                <span>File Type</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {doc.file_type?.toUpperCase() || 'DOC'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Size</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {formatFileSize(doc.bytes_total)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Uploaded</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {formatDate(doc.created_at)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Status</span>
                <span
                  className="font-medium"
                  style={{ color: doc.status === 'processed' ? DVA_COLORS.success : DVA_COLORS.warning }}
                >
                  {doc.status === 'processed' ? 'Ready' : doc.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Signals extracted</span>
                <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                  {signalsFound}
                </span>
              </div>
              {Number.isFinite(factTypes) && factTypes > 0 && (
                <div className="flex justify-between">
                  <span>Signal types</span>
                  <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                    {factTypes}
                  </span>
                </div>
              )}
              {Number.isFinite(sectionCount) && sectionCount > 0 && (
                <div className="flex justify-between">
                  <span>Sections</span>
                  <span className="font-medium" style={{ color: DVA_COLORS.navy }}>
                    {sectionCount}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Extracted Signals — full list from API `signals` */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: DVA_COLORS.orange }} />
              <h3 className="text-sm font-semibold" style={{ color: DVA_COLORS.navy }}>
                Extracted Signals
              </h3>
              {signalsFound > 0 && (
                <span
                  className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: DVA_COLORS.orangeLight, color: DVA_COLORS.orange }}
                >
                  {signalsFound}
                </span>
              )}
            </div>

            {signals.length > 0 ? (
              <ul className="space-y-2">
                {signals.map((signal, idx) => {
                  const typeLabel = formatSignalType(signal.fact_type || signal.type || signal.key);
                  const confidencePct =
                    signal.confidence != null && Number.isFinite(Number(signal.confidence))
                      ? Math.round(Number(signal.confidence) * (Number(signal.confidence) <= 1 ? 100 : 1))
                      : null;
                  const metaParts = [
                    signal.page_no != null && Number(signal.page_no) > 0 ? `p. ${signal.page_no}` : null,
                    signal.year != null && Number(signal.year) > 0 ? String(signal.year) : null,
                    confidencePct != null ? `${confidencePct}% conf.` : null,
                  ].filter(Boolean);

                  return (
                    <li
                      key={signal.fact_id || signal.id || `signal-${idx}`}
                      className="p-3 rounded-xl border"
                      style={{ backgroundColor: DVA_COLORS.slateLight, borderColor: DVA_COLORS.border }}
                    >
                      <div className="flex items-start gap-2 mb-1.5">
                        <span
                          className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                          style={{ backgroundColor: '#fff', color: DVA_COLORS.navy, border: `1px solid ${DVA_COLORS.border}` }}
                        >
                          {typeLabel}
                        </span>
                        {metaParts.length > 0 && (
                          <span className="text-[10px] ml-auto shrink-0" style={{ color: DVA_COLORS.slate }}>
                            {metaParts.join(' · ')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm leading-snug" style={{ color: DVA_COLORS.navy }}>
                        {signal.label || signal.text || signal.description || 'Untitled signal'}
                      </p>
                    </li>
                  );
                })}
              </ul>
            ) : signalsFound > 0 ? (
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: DVA_COLORS.slateLight }}
              >
                <p className="text-sm" style={{ color: DVA_COLORS.navy }}>
                  <span className="font-semibold" style={{ color: DVA_COLORS.orange }}>
                    {signalsFound} signal{signalsFound === 1 ? '' : 's'}
                  </span>
                  {' '}extracted from this document
                  {Number.isFinite(factTypes) && factTypes > 0
                    ? ` across ${factTypes} type${factTypes === 1 ? '' : 's'}`
                    : ''}
                  . Signal details were not included in this response.
                </p>
              </div>
            ) : (
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: DVA_COLORS.slateLight }}
              >
                <p className="text-sm" style={{ color: DVA_COLORS.slate }}>
                  No strategic signals were extracted from this document.
                </p>
              </div>
            )}
          </div>

          {/* How this helps */}
          <div
            className="p-4 rounded-xl"
            style={{ backgroundColor: DVA_COLORS.orangeLight }}
          >
            <p className="text-xs" style={{ color: DVA_COLORS.slate }}>
              These signals are used to enhance opportunity rankings, refine ROI assumptions, and strengthen executive defensibility.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// REMOVE CONFIRMATION MODAL
// ============================================================
function RemoveConfirmModal({ doc, isOpen, onClose, onConfirm }) {
  if (!isOpen || !doc) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: DVA_COLORS.errorLight }}
          >
            <Trash2 className="w-6 h-6" style={{ color: DVA_COLORS.error }} />
          </div>

          <h3 className="text-lg font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
            Remove Document?
          </h3>
          <p className="text-sm mb-6" style={{ color: DVA_COLORS.slate }}>
            This will remove "<span className="font-medium">{doc.title || doc.filename}</span>" and its extracted signals from your analysis.
          </p>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors"
              style={{
                borderColor: DVA_COLORS.border,
                color: DVA_COLORS.slate
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm(doc.document_id);
                onClose();
              }}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: DVA_COLORS.error }}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PROGRESS INDICATOR COMPONENT
// ============================================================
const RECOMMENDED_DOCUMENT_COUNT = 3;

function formatDocumentsProgressLabel(count) {
  if (count < RECOMMENDED_DOCUMENT_COUNT) {
    return `${count} of ${RECOMMENDED_DOCUMENT_COUNT} recommended`;
  }
  return `${count} document${count === 1 ? '' : 's'} uploaded`;
}

function DocumentsProgressIndicator({ documentCount, pendingCount = 0 }) {
  const getProgress = (count) => {
    if (count === 0) return 0;
    if (count === 1) return 33;
    if (count === 2) return 66;
    return 100;
  };

  const totalCount = documentCount + pendingCount;
  const progress = getProgress(totalCount);

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: DVA_COLORS.border }}>
        <div
          className="h-1.5 rounded-full transition-all duration-500"
          style={{
            width: `${progress}%`,
            backgroundColor: progress === 100 ? DVA_COLORS.success : DVA_COLORS.orange
          }}
        />
      </div>
      <span className="text-xs font-medium" style={{ color: DVA_COLORS.slate }}>
        {progress}%
      </span>
    </div>
  );
}
// ============================================================
// UPLOAD FILE CARD (for upload queue)
// ============================================================
function UploadFileCard({ file, onRemove, onRetry }) {
  const statusConfig = {
    'ready': { icon: Clock, label: 'Queued', color: DVA_COLORS.slate, bgColor: DVA_COLORS.slateLight },
    'uploading': { icon: Loader, label: 'Uploading...', color: DVA_COLORS.orange, bgColor: DVA_COLORS.orangeLight },
    'processing': { icon: Loader, label: 'Processing...', color: DVA_COLORS.orange, bgColor: DVA_COLORS.orangeLight },
    'complete': { icon: CheckCircle, label: 'Complete', color: DVA_COLORS.success, bgColor: DVA_COLORS.successLight },
    'error': { icon: XCircle, label: 'Processing failed', color: DVA_COLORS.error, bgColor: DVA_COLORS.errorLight },
  };

  const config = statusConfig[file.status] || statusConfig['ready'];
  const StatusIcon = config.icon;
  const isProcessing = file.status === 'uploading' || file.status === 'processing';
  const isError = file.status === 'error';
  const isComplete = file.status === 'complete';

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div
      className="bg-white rounded-xl border-2 p-4 transition-all"
      style={{
        borderColor: isError ? DVA_COLORS.error :
          isComplete ? DVA_COLORS.success :
            isProcessing ? DVA_COLORS.orange : DVA_COLORS.border,
        backgroundColor: isProcessing ? DVA_COLORS.orangeLight : 'white',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: config.bgColor }}
        >
          <FileText className="w-5 h-5" style={{ color: config.color }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" style={{ color: DVA_COLORS.navy }}>
                {file.name}
              </p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
                  {formatFileSize(file.size)}
                </span>
                <div className="flex items-center gap-1.5" style={{ color: config.color }}>
                  <StatusIcon className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`} />
                  <span className="text-xs font-medium">{file.statusLabel || config.label}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Retry button for failed uploads */}
              {isError && onRetry && (
                <button
                  onClick={() => onRetry(file.id)}
                  className="px-2 py-1 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: DVA_COLORS.orangeLight,
                    color: DVA_COLORS.orange
                  }}
                >
                  Retry
                </button>
              )}

              <button
                onClick={() => onRemove(file.id)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                disabled={isProcessing}
                style={{ opacity: isProcessing ? 0.5 : 1 }}
              >
                <X className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {isProcessing && (
            <div className="mt-3">
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${file.progress}%`,
                    backgroundColor: DVA_COLORS.orange,
                  }}
                />
              </div>
              <p className="text-xs mt-1" style={{ color: DVA_COLORS.orange }}>
                {file.status === 'uploading' ? 'Uploading to server...' : (file.statusLabel || 'Processing...')}
              </p>
            </div>
          )}

          {/* Success message */}
          {isComplete && (
            <div className="mt-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: DVA_COLORS.success }} />
              <span className="text-xs" style={{ color: DVA_COLORS.success }}>
                Processing complete. Document is ready.
              </span>
            </div>
          )}

          {/* Error message with details */}
          {isError && file.error && (
            <div className="mt-2 p-2 rounded-lg" style={{ backgroundColor: DVA_COLORS.errorLight }}>
              <p className="text-xs" style={{ color: DVA_COLORS.error }}>
                {file.error}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const DocumentManagementPage = ({ clientId: propClientId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};

  // EA Focus Mode
  const { isEA, returnTo, restoreScrollY } = useEAFocusMode();

  // Auth & Client — EA Personalize progress uses EA_CLIENT_ID; uploads must match
  // or the Document Upload card stays at 0% after successful uploads.
  const { session, sessionLoading } = useAuth();
  const sessionClientId = session?.client_id || null;
  const clientId = isEA
    ? (locationState.clientId || EA_CLIENT_ID)
    : (propClientId || sessionClientId);

  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upload');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [detailsDoc, setDetailsDoc] = useState(null);
  const [removeDoc, setRemoveDoc] = useState(null);
  const [isDropzoneProcessing, setIsDropzoneProcessing] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const latestStatusSignatureRef = useRef({});

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  const syncQueueFromDocuments = useCallback((nextDocuments) => {
    if (!Array.isArray(nextDocuments) || nextDocuments.length === 0) return;

    const statusByDocumentId = new Map(
      nextDocuments.map((doc) => [String(doc.document_id), doc.status])
    );

    setFiles((prev) =>
      prev.map((file) => {
        if (!file.documentId) return file;
        const backendStatus = statusByDocumentId.get(String(file.documentId));
        if (!backendStatus) return file;

        const queueMeta = getQueueStatusMeta(backendStatus);
        return {
          ...file,
          ...queueMeta,
        };
      })
    );
  }, []);

  // ============================================================
  // FETCH DOCUMENTS
  // ============================================================
  const fetchDocuments = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v2/documents?clientId=${clientId}&document_kind=strategy`);
      if (response.ok) {
        const data = await response.json();
        const nextDocuments = data.data || data.documents || [];
        setDocuments(nextDocuments);
        syncQueueFromDocuments(nextDocuments);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, syncQueueFromDocuments]);

  useEffect(() => {
    if (!clientId) return;
    // Fetch on initial load to get accurate count for progress indicator
    fetchDocuments();
  }, [clientId, fetchDocuments]);

  useEffect(() => {
    if (!clientId) return;
    if (activeTab === 'library') {
      fetchDocuments();
    }
  }, [activeTab, clientId, fetchDocuments]);

  useEffect(() => {
    if (!clientId) return;

    if (!SOCKET_BASE_URL) return;
    const socket = io(SOCKET_BASE_URL, {
      path: SOCKET_PATH,
      transports: ['polling', 'websocket'],
      withCredentials: true,
    });
    socketRef.current = socket;

    const handleStatusEvent = (payload) => {
      const eventClientId = String(payload?.client_id || '');
      const eventDocumentId = String(payload?.document_id || '');
      const eventStatus = normalizeIncomingStatus(payload?.status);
      const eventUpdatedAt = String(payload?.updated_at || '');

      if (!eventClientId || !eventDocumentId || !eventStatus) return;
      if (String(clientId) !== eventClientId) return;

      const eventSignature = `${eventStatus}|${eventUpdatedAt}`;
      if (latestStatusSignatureRef.current[eventDocumentId] === eventSignature) return;
      latestStatusSignatureRef.current[eventDocumentId] = eventSignature;

      if (IS_DEV_MODE) {
        console.log('[DocumentManagement socket] status event', {
          eventDocumentId,
          eventClientId,
          eventStatus,
          eventUpdatedAt,
        });
      }

      setDocuments((prev) =>
        prev.map((doc) =>
          String(doc.document_id) === eventDocumentId
            ? mergeRealtimeStatusIntoDocument(doc, eventStatus, eventUpdatedAt)
            : doc
        )
      );

      const queueMeta = getQueueStatusMeta(eventStatus);
      setFiles((prev) =>
        prev.map((file) =>
          String(file.documentId) === eventDocumentId
            ? {
              ...file,
              ...queueMeta,
            }
            : file
        )
      );
    };

    const onConnect = () => {
      setSocketConnected(true);
      if (IS_DEV_MODE) {
        console.log('[DocumentManagement socket] connected', {
          socketId: socket.id,
          clientId,
        });
      }
      socket.emit('document:subscribe', { clientId });
    };

    const onDisconnect = (reason) => {
      setSocketConnected(false);
      if (IS_DEV_MODE) {
        console.log('[DocumentManagement socket] disconnected', { reason, clientId });
      }
      // Keep API fallback without interval polling: refresh once when socket drops.
      fetchDocuments();
    };

    const onConnectError = (error) => {
      setSocketConnected(false);
      if (IS_DEV_MODE) {
        console.warn('[DocumentManagement socket] connection error', {
          clientId,
          message: error?.message || String(error),
        });
      }
      // Keep API fallback without interval polling: refresh once on connection error.
      fetchDocuments();
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('document:status-changed', handleStatusEvent);
    socket.on('documentStatusChanged', handleStatusEvent);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('document:status-changed', handleStatusEvent);
      socket.off('documentStatusChanged', handleStatusEvent);
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
      latestStatusSignatureRef.current = {};
    };
  }, [clientId, fetchDocuments]);

  // ============================================================
  // FILE HANDLING
  // ============================================================
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    const allowedTypes = ['.txt', '.csv', '.pdf', '.md'];
    const maxSize = 25 * 1024 * 1024;

    const newFiles = Array.from(fileList).map(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      let status = 'ready';
      let error = null;

      if (!allowedTypes.includes(extension)) {
        status = 'error';
        error = 'File type not supported. Use TXT, CSV, PDF, or MD.';
      } else if (file.size > maxSize) {
        status = 'error';
        error = 'File size exceeds 25MB limit.';
      }

      return {
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        size: file.size,
        type: extension,
        status,
        statusLabel: status === 'ready' ? 'Queued' : 'Validation failed',
        error,
        progress: 0,
        documentId: null
      };
    });

    setFiles(prev => [...prev, ...newFiles]);

    // Auto-upload valid files after a brief delay to show them in the queue
    const validFiles = newFiles.filter(f => f.status === 'ready');
    if (validFiles.length > 0) {
      setIsDropzoneProcessing(true);
      setTimeout(() => {
        uploadFilesSequentially(validFiles);
      }, 500);
    }
  };

  // Upload files one by one
  const uploadFilesSequentially = async (filesToUpload) => {
    setUploading(true);

    for (const fileItem of filesToUpload) {
      await uploadFile(fileItem);
    }

    setUploading(false);
    setIsDropzoneProcessing(false);

    // Refresh document list after uploads complete
    fetchDocuments();
  };

  // Retry a failed upload
  const retryFile = async (fileId) => {
    const fileToRetry = files.find(f => f.id === fileId);
    if (!fileToRetry) return;

    // Reset the file status
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, status: 'ready', error: null, progress: 0, statusLabel: 'Queued' } : f
    ));

    // Re-upload
    setTimeout(() => {
      uploadFile({ ...fileToRetry, status: 'ready', error: null, progress: 0 });
    }, 100);
  };

  const uploadFile = async (fileItem) => {
    if (!clientId) return;

    const formData = new FormData();
    formData.append('file', fileItem.file);
    formData.append('document_kind', 'strategy');

    try {
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading', progress: 25, statusLabel: 'Uploading...' } : f
      ));

      const uploadResponse = await fetch(`${API_BASE_URL}/api/v2/documents/upload?clientId=${clientId}`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Upload failed (${uploadResponse.status})`);
      }

      const uploadResult = await uploadResponse.json();
      const documentId = uploadResult.data?.document_id;
      const initialBackendStatus = uploadResult.data?.status || uploadResult.status || 'scanning';

      if (!documentId) {
        throw new Error('No document ID returned from server');
      }

      const queueMeta = getQueueStatusMeta(initialBackendStatus);
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, documentId: String(documentId), ...queueMeta } : f
      ));
      await fetchDocuments();

    } catch (error) {
      console.error('Upload error:', error);
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? {
          ...f,
          status: 'error',
          statusLabel: 'Upload failed',
          error: error.message || 'Upload failed. Please try again.',
          progress: 0
        } : f
      ));
    }
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const getDocumentDownloadName = (doc) => {
    if (doc?.filename) return doc.filename;

    const safeTitle = (doc?.title || `document-${doc?.document_id || 'file'}`).trim();
    const ext = (doc?.file_type || '').toString().trim().toLowerCase();

    if (ext && !safeTitle.toLowerCase().endsWith(`.${ext}`)) {
      return `${safeTitle}.${ext}`;
    }

    return safeTitle;
  };

  const downloadDocument = async (doc) => {
    const directUrl =
      doc?.downloadUrl ||
      doc?.download_url ||
      doc?.file_url ||
      doc?.fileUrl ||
      doc?.signed_url ||
      doc?.signedUrl ||
      doc?.public_url ||
      doc?.publicUrl;

    const downloadExpiry = doc?.download_url_expires_at
      ? new Date(doc.download_url_expires_at)
      : null;
    const isDownloadExpired =
      downloadExpiry &&
      !Number.isNaN(downloadExpiry.getTime()) &&
      downloadExpiry.getTime() <= Date.now();

    const fileName = getDocumentDownloadName(doc);

    if (isDownloadExpired) {
      alert('This download link has expired. Please refresh the document list to get a new link.');
      return;
    }

    if (directUrl) {
      const link = document.createElement('a');
      link.href = directUrl;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    alert('Download link is not available for this document yet.');
  };

  const handleRemoveDocument = async (documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/documents/${documentId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setDocuments(prev => prev.filter(d => d.document_id !== documentId));
      }
    } catch (error) {
      console.error('Error removing document:', error);
    }
  };

  // ============================================================
  // LOADING / ERROR STATES (after all hooks)
  // ============================================================
  if (sessionLoading && !propClientId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: DVA_COLORS.slateLight }}>
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: DVA_COLORS.navy }} />
          <p style={{ color: DVA_COLORS.slate }}>Loading your session...</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: DVA_COLORS.slateLight }}>
        <div className="text-center max-w-md px-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: DVA_COLORS.orangeLight }}
          >
            <AlertCircle className="w-8 h-8" style={{ color: DVA_COLORS.orange }} />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
            No Organization Selected
          </h2>
          <p className="mb-6" style={{ color: DVA_COLORS.slate }}>
            Your account is not currently associated with an organization.
            Please contact your administrator or complete onboarding.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-2.5 rounded-xl text-white font-medium transition-colors"
            style={{ backgroundColor: DVA_COLORS.navy }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen" style={{ backgroundColor: DVA_COLORS.slateLight }}>
      {/* EA Focus Banner */}
      {isEA && (
        <EAFocusBanner
          returnTo={returnTo}
          restoreScrollY={restoreScrollY}
        />
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: DVA_COLORS.navy }}>
            Document Management
          </h1>
          <p className="mt-2 text-sm" style={{ color: DVA_COLORS.slate }}>
            Add institutional context that strengthens rankings, ROI assumptions, and executive defensibility.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b mb-6" style={{ borderColor: DVA_COLORS.border }}>
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              style={{
                borderColor: activeTab === 'upload' ? DVA_COLORS.orange : 'transparent',
                color: activeTab === 'upload' ? DVA_COLORS.navy : DVA_COLORS.slate,
              }}
            >
              Upload Documents
            </button>
            <button
              onClick={() => setActiveTab('library')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              style={{
                borderColor: activeTab === 'library' ? DVA_COLORS.orange : 'transparent',
                color: activeTab === 'library' ? DVA_COLORS.navy : DVA_COLORS.slate,
              }}
            >
              Document Library ({documents.length})
            </button>
          </nav>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6 p-4 bg-white rounded-xl border" style={{ borderColor: DVA_COLORS.border }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: DVA_COLORS.slate }}>
              Documents Progress
            </span>
            <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
              {formatDocumentsProgressLabel(
                documents.length + files.filter(f => f.status === 'complete').length
              )}
            </span>
          </div>
          <DocumentsProgressIndicator
            documentCount={documents.length}
            pendingCount={files.filter(f => f.status === 'complete').length}
          />
        </div>

        {/* Content */}
        {activeTab === 'upload' ? (
          <div className="space-y-6">
            {/* Upload Dropzone */}
            <div
              className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer`}
              style={{
                borderColor: dragActive ? DVA_COLORS.orange : DVA_COLORS.border,
                backgroundColor: dragActive ? DVA_COLORS.orangeLight :
                  isDropzoneProcessing ? DVA_COLORS.slateLight : 'white',
                transform: dragActive ? 'scale(1.01)' : 'scale(1)',
                boxShadow: dragActive ? '0 8px 25px rgba(255, 110, 76, 0.15)' : 'none',
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                accept=".txt,.csv,.pdf,.md"
                onChange={handleChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isDropzoneProcessing}
              />

              {isDropzoneProcessing ? (
                /* Processing State */
                <>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: DVA_COLORS.orangeLight }}
                  >
                    <Loader className="w-8 h-8 animate-spin" style={{ color: DVA_COLORS.orange }} />
                  </div>
                  <p className="text-base font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
                    Processing your documents...
                  </p>
                  <p className="text-sm" style={{ color: DVA_COLORS.slate }}>
                    Uploading and extracting strategic signals
                  </p>
                </>
              ) : dragActive ? (
                /* Drag Active State */
                <>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: DVA_COLORS.orange }}
                  >
                    <Upload className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-base font-semibold mb-2" style={{ color: DVA_COLORS.orange }}>
                    Drop files to upload
                  </p>
                  <p className="text-sm" style={{ color: DVA_COLORS.slate }}>
                    Release to begin processing
                  </p>
                </>
              ) : (
                /* Idle State */
                <>
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: DVA_COLORS.slateLight }}
                  >
                    <Upload className="w-8 h-8" style={{ color: DVA_COLORS.navy }} />
                  </div>

                  <p className="text-base font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
                    Drop strategic documents here to strengthen prioritization and ROI confidence.
                  </p>
                  <p className="text-sm mb-4" style={{ color: DVA_COLORS.slate }}>
                    Examples: Strategic plan, annual report, governance policy, transformation roadmap.
                  </p>
                  <p className="text-xs" style={{ color: DVA_COLORS.slate }}>
                    Supported formats: TXT, CSV, PDF, MD (Max 25MB)
                  </p>
                </>
              )}
            </div>

            {/* How It Works - Collapsible */}
            <div className="bg-white rounded-xl border" style={{ borderColor: DVA_COLORS.border }}>
              <button
                onClick={() => setShowHowItWorks(!showHowItWorks)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
                  <span className="text-sm font-medium" style={{ color: DVA_COLORS.navy }}>
                    How DVA uses documents
                  </span>
                </div>
                {showHowItWorks ? (
                  <ChevronUp className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
                ) : (
                  <ChevronDown className="w-4 h-4" style={{ color: DVA_COLORS.slate }} />
                )}
              </button>

              {showHowItWorks && (
                <div className="px-4 pb-4">
                  <ol className="text-sm space-y-2" style={{ color: DVA_COLORS.slate }}>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>1.</span>
                      Upload strategic documents, reports, or data files
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>2.</span>
                      Documents are scanned for viruses and processed
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>3.</span>
                      Key facts, metrics, and strategic signals are extracted
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold" style={{ color: DVA_COLORS.navy }}>4.</span>
                      Information enhances rankings, ROI, and defensibility
                    </li>
                  </ol>
                </div>
              )}
            </div>

            {/* File Queue / Recent Uploads */}
            {files.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold" style={{ color: DVA_COLORS.navy }}>
                    Recent Uploads
                  </h3>
                  {files.some(f => f.status === 'complete') && (
                    <button
                      onClick={() => {
                        setActiveTab('library');
                        fetchDocuments();
                      }}
                      className="text-xs font-medium transition-colors"
                      style={{ color: DVA_COLORS.orange }}
                    >
                      View in library →
                    </button>
                  )}
                </div>
                {files.map(file => (
                  <UploadFileCard
                    key={file.id}
                    file={file}
                    onRemove={removeFile}
                    onRetry={retryFile}
                  />
                ))}

                {/* Retry All Failed Button */}
                {files.some(f => f.status === 'error') && !uploading && (
                  <button
                    onClick={() => {
                      const failedFiles = files.filter(f => f.status === 'error' && !f.error?.includes('not supported') && !f.error?.includes('exceeds'));
                      failedFiles.forEach(f => retryFile(f.id));
                    }}
                    className="w-full py-3 rounded-xl font-semibold transition-colors"
                    style={{
                      backgroundColor: DVA_COLORS.orangeLight,
                      color: DVA_COLORS.orange,
                    }}
                  >
                    Retry Failed Uploads ({files.filter(f => f.status === 'error').length})
                  </button>
                )}

                {/* Clear Completed Button */}
                {files.some(f => f.status === 'complete') && !uploading && (
                  <button
                    onClick={() => {
                      setFiles(prev => prev.filter(f => f.status !== 'complete'));
                      fetchDocuments(); // Refresh library
                    }}
                    className="w-full py-2 rounded-xl text-sm font-medium transition-colors"
                    style={{
                      color: DVA_COLORS.slate,
                      backgroundColor: DVA_COLORS.slateLight,
                    }}
                  >
                    Clear completed uploads
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Document Library */
          <div>
            {loading ? (
              <div className="py-12 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: DVA_COLORS.orange }} />
                <p style={{ color: DVA_COLORS.slate }}>Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              /* Empty State */
              <div className="py-12 text-center bg-white rounded-2xl border" style={{ borderColor: DVA_COLORS.border }}>
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: DVA_COLORS.slateLight }}
                >
                  <FileText className="w-8 h-8" style={{ color: DVA_COLORS.slate }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: DVA_COLORS.navy }}>
                  No documents uploaded yet.
                </h3>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: DVA_COLORS.slate }}>
                  Uploading 2–3 strategic documents materially improves defensibility and ranking accuracy.
                </p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="px-6 py-2.5 rounded-xl text-white font-medium transition-colors"
                  style={{ backgroundColor: DVA_COLORS.navy }}
                >
                  Upload a document
                </button>
              </div>
            ) : (
              /* Document Cards */
              <div className="space-y-4">
                {documents.map((doc) => (
                  <DocumentCard
                    key={doc.document_id}
                    doc={doc}
                    onViewDetails={(doc) => setDetailsDoc(doc)}
                    onDownload={downloadDocument}
                    onRemove={(doc) => setRemoveDoc(doc)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Details Modal */}
      <DocumentDetailsModal
        doc={detailsDoc}
        isOpen={!!detailsDoc}
        onClose={() => setDetailsDoc(null)}
      />

      {/* Remove Confirmation Modal */}
      <RemoveConfirmModal
        doc={removeDoc}
        isOpen={!!removeDoc}
        onClose={() => setRemoveDoc(null)}
        onConfirm={handleRemoveDocument}
      />
    </div>
  );
};

export default DocumentManagementPage;